import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, ChevronLeft, ChevronRight, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SyncBadge, ModeBadge } from "@/components/common/SyncBadge";
import { COUNTRIES, LANGUAGES, TIMEZONES, money, title } from "@/lib/format";
import { cn } from "@/lib/utils";

const STEPS = ["Basics", "AI agent", "Contacts", "Telephony", "Schedule", "Budget", "Review"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function CampaignWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    provider_mode: "live",
    name: "", product: "", description: "", country: "AL", language: "sq",
    timezone: "Europe/Tirane", agent_id: "", agent_version: null, phone_number: "",
    telephony_provider: "mmdsmart", voice_provider: "vapi",
    limits: { max_concurrent_calls: 4, calls_per_minute: 12, max_call_duration: 300 },
    schedule: {
      start_date: new Date().toISOString().slice(0, 10), end_date: "", weekdays: [0, 1, 2, 3, 4],
      start_hour: "09:00", end_hour: "20:00", respect_contact_timezone: true,
      retry_no_answer: true, max_retries: 2, voicemail_behavior: "leave_message",
    },
    budget: { daily_budget: 40, total_budget: 500, max_calls: 800, max_cost_per_call: 0.4, auto_pause_on_budget: true },
    contact_ids: [],
  });
  const [importReport, setImportReport] = useState(null);
  const [mapping, setMapping] = useState({});
  const [csvFile, setCsvFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testPhone, setTestPhone] = useState("");

  const { data: agents } = useQuery({ queryKey: ["agents-list"], queryFn: () => get("/agents", { page_size: 100 }) });
  const { data: numbers } = useQuery({ queryKey: ["numbers"], queryFn: () => get("/phone-numbers") });
  const { data: voices } = useQuery({ queryKey: ["voices"], queryFn: () => get("/voices") });

  const selectedAgent = (agents?.items || []).find((a) => a.id === form.agent_id);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setNested = (key, patch) => setForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));

  const estimate = useMemo(() => {
    const count = form.contact_ids.length;
    return {
      contacts: count,
      max: count * Number(form.budget.max_cost_per_call || 0),
      expected: count * Number(form.budget.max_cost_per_call || 0) * 0.55,
    };
  }, [form.contact_ids, form.budget.max_cost_per_call]);

  const errors = useMemo(() => {
    const list = [];
    if (!form.name.trim()) list.push("Campaign name is required");
    if (!form.product.trim()) list.push("Product or service is required");
    if (!form.agent_id) list.push("An AI agent must be selected");
    if (!form.phone_number) list.push("An outbound phone number must be selected");
    if (!form.contact_ids.length) list.push("At least one valid contact must be imported");
    if (!form.schedule.start_date) list.push("A start date is required");
    if (!form.schedule.weekdays.length) list.push("Select at least one calling weekday");
    if (!Number(form.budget.total_budget)) list.push("A total campaign budget is required");
    return list;
  }, [form]);

  const uploadPreview = async (file) => {
    setCsvFile(file);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/contacts/import/preview", body);
      setPreview(data);
      setMapping(data.suggested_mapping || {});
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const runImport = useMutation({
    mutationFn: async () => {
      const body = new FormData();
      body.append("file", csvFile);
      const { data } = await api.post("/contacts/import", body, {
        params: {
          mapping: JSON.stringify(mapping),
          default_country: form.country,
          default_language: form.language,
          source: "campaign_wizard",
        },
      });
      return data;
    },
    onSuccess: (data) => {
      const ids = data.contact_ids || [];
      setImportReport(data.stats);
      set({ contact_ids: ids });
      toast.success(`${ids.length} contacts ready for this campaign`);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const useExisting = useMutation({
    mutationFn: () => get("/contacts", { page_size: 100, country: form.country, do_not_call: false }),
    onSuccess: (data) => {
      const ids = (data.items || []).filter((c) => !c.do_not_call).map((c) => c.id);
      set({ contact_ids: ids });
      setImportReport({ total: ids.length, valid: ids.length, invalid: 0, duplicates: 0, blocked: 0 });
      toast.success(`${ids.length} existing contacts selected for ${form.country}`);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const testCall = useMutation({
    mutationFn: (mode) => api.post(`/agents/${form.agent_id}/test`, { mode, phone: testPhone }),
    onSuccess: ({ data }) => {
      setTestResult(data);
      toast.success(data.mode === "phone" ? "Live test call queued to your phone" : "Browser test completed");
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const save = useMutation({
    mutationFn: (status) =>
      api.post("/campaigns", {
        ...form,
        agent_version: form.agent_version || selectedAgent?.published_version || 1,
        status,
      }),
    onSuccess: ({ data }) => {
      toast.success("Campaign saved");
      navigate(`/campaigns/${data.id}`);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const canNext = () => {
    if (step === 0) return form.name.trim() && form.product.trim();
    if (step === 1) return !!form.agent_id;
    if (step === 2) return form.contact_ids.length > 0;
    if (step === 3) return !!form.phone_number;
    return true;
  };

  return (
    <>
      <PageHeader
        testId="campaign-wizard-page"
        title="Create campaign"
        subtitle="Seven guided steps from basics to launch"
        actions={
          <Button variant="outline" onClick={() => navigate("/campaigns")} data-testid="wizard-cancel">
            Cancel
          </Button>
        }
      />

      <ol className="panel flex flex-wrap gap-2 p-3" data-testid="wizard-steps">
        {STEPS.map((label, index) => (
          <li key={label} className="flex items-center gap-2">
            <button
              type="button"
              data-testid={`wizard-step-${index}`}
              onClick={() => index <= step && setStep(index)}
              className={cn(
                "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors duration-150",
                index === step ? "bg-blue-50 font-medium text-blue-700" : index < step ? "text-slate-700 hover:bg-slate-50" : "text-slate-400"
              )}
            >
              <span
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold",
                  index < step ? "bg-emerald-100 text-emerald-700" : index === step ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"
                )}
              >
                {index < step ? <Check className="h-3 w-3" /> : index + 1}
              </span>
              {label}
            </button>
            {index < STEPS.length - 1 ? <ChevronRight className="h-3.5 w-3.5 text-slate-300" /> : null}
          </li>
        ))}
      </ol>

      <Section testId="wizard-body" title={`Step ${step + 1} · ${STEPS[step]}`}>
        <div className="space-y-5 p-4">
          {step === 0 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Campaign name</Label>
                <Input data-testid="wizard-name" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Fiber Q3 Tirana" />
              </div>
              <div className="space-y-1.5">
                <Label>Product / service</Label>
                <Input data-testid="wizard-product" value={form.product} onChange={(e) => set({ product: e.target.value })} placeholder="Fiber Internet 500Mbps" />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Description</Label>
                <Textarea data-testid="wizard-description" rows={3} value={form.description} onChange={(e) => set({ description: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Country</Label>
                <Select value={form.country} onValueChange={(v) => set({ country: v })}>
                  <SelectTrigger data-testid="wizard-country"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {COUNTRIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Language</Label>
                <Select value={form.language} onValueChange={(v) => set({ language: v })}>
                  <SelectTrigger data-testid="wizard-language"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Timezone</Label>
                <Select value={form.timezone} onValueChange={(v) => set({ timezone: v })}>
                  <SelectTrigger data-testid="wizard-timezone"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : null}

          {step === 1 ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>AI agent</Label>
                  <Select value={form.agent_id} onValueChange={(v) => {
                    const picked = (agents?.items || []).find((a) => a.id === v);
                    setForm((current) => ({
                      ...current,
                      agent_id: v,
                      agent_version: null,
                      phone_number: current.phone_number || picked?.phone_number || "",
                    }));
                  }}>
                    <SelectTrigger data-testid="wizard-agent"><SelectValue placeholder="Select an agent" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {(agents?.items || []).filter((a) => a.published_version).map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name} · v{a.published_version}{a.phone_number ? ` · ${a.phone_number}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Agent version</Label>
                  <Select
                    value={String(form.agent_version || selectedAgent?.published_version || "")}
                    onValueChange={(v) => set({ agent_version: Number(v) })}
                    disabled={!selectedAgent}
                  >
                    <SelectTrigger data-testid="wizard-agent-version"><SelectValue placeholder="Latest published" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {Array.from({ length: selectedAgent?.published_version || 0 }).map((_, i) => (
                        <SelectItem key={i} value={String(i + 1)}>Version {i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {selectedAgent ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                  <p className="font-medium text-slate-800">
                    {selectedAgent.name} — {selectedAgent.company_name}
                  </p>
                  <p className="mt-1 text-slate-600">
                    Voice: {(voices || []).find((v) => v.id === selectedAgent.voice_id)?.name || selectedAgent.voice_id} ·
                    Tone: {title(selectedAgent.tone)} · Model: {selectedAgent.model}
                  </p>
                  <p className="mt-2 text-slate-600">
                    Opening: “{selectedAgent.conversation?.opening_message}”
                  </p>
                </div>
              ) : null}

              <div className="flex flex-wrap items-end gap-2">
                <Button variant="outline" disabled={!form.agent_id} onClick={() => testCall.mutate("browser")} data-testid="wizard-browser-test">
                  Run browser test
                </Button>
                <div className="space-y-1.5">
                  <Label>Call my phone</Label>
                  <Input
                    data-testid="wizard-test-phone"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="+355 69 000 0000"
                    className="w-52"
                  />
                </div>
                <Button
                  variant="outline"
                  disabled={!form.agent_id || !testPhone}
                  onClick={() => testCall.mutate("phone")}
                  data-testid="wizard-phone-test"
                >
                  Call my phone
                </Button>
              </div>

              {testResult ? (
                <div className="rounded-lg border border-slate-200 p-3" data-testid="wizard-test-result">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Simulated conversation
                  </p>
                  <div className="mt-2 space-y-2">
                    {testResult.transcript.map((seg, i) => (
                      <div key={i} className={cn("flex", seg.speaker === "customer" ? "justify-end" : "justify-start")}>
                        <p className={cn("max-w-md rounded-lg px-3 py-1.5 text-sm",
                          seg.speaker === "customer" ? "bg-slate-100 text-slate-700" : "bg-blue-50 text-blue-900")}>
                          {seg.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {step === 2 ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 transition-colors duration-150 hover:bg-slate-50">
                  <Upload className="h-4 w-4" />
                  {csvFile ? csvFile.name : "Upload CSV"}
                  <input
                    data-testid="wizard-csv-input"
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && uploadPreview(e.target.files[0])}
                  />
                </label>
                <Button variant="outline" onClick={() => useExisting.mutate()} data-testid="wizard-use-existing">
                  Use existing contacts ({form.country})
                </Button>
              </div>

              {preview ? (
                <div className="space-y-3 rounded-lg border border-slate-200 p-3">
                  <p className="text-sm text-slate-600">
                    {preview.row_count} rows detected. Map the CSV columns:
                  </p>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    {["first_name", "last_name", "phone", "country", "language", "product_interest"].map((field) => (
                      <div key={field} className="space-y-1.5">
                        <Label>{title(field)}{field === "phone" ? " *" : ""}</Label>
                        <Select
                          value={mapping[field] || "none"}
                          onValueChange={(v) => setMapping((m) => ({ ...m, [field]: v === "none" ? undefined : v }))}
                        >
                          <SelectTrigger data-testid={`wizard-map-${field}`}><SelectValue placeholder="Not mapped" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="none">Not mapped</SelectItem>
                            {preview.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  <Button
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={!mapping.phone || runImport.isPending}
                    onClick={() => runImport.mutate()}
                    data-testid="wizard-run-import"
                  >
                    {runImport.isPending ? "Importing…" : "Validate and import"}
                  </Button>
                </div>
              ) : null}

              {importReport ? (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="wizard-import-report">
                  {[
                    ["Total rows", importReport.total, "text-slate-900"],
                    ["Valid", importReport.valid, "text-emerald-600"],
                    ["Invalid numbers", importReport.invalid, "text-amber-600"],
                    ["Duplicates", importReport.duplicates, "text-amber-600"],
                    ["Blocked (DNC)", importReport.blocked, "text-rose-600"],
                  ].map(([label, value, tone]) => (
                    <div key={label} className="rounded-lg border border-slate-200 px-3 py-2">
                      <p className="kpi-label">{label}</p>
                      <p className={cn("text-xl font-semibold", tone)}>{value}</p>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {step === 3 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-1.5 md:col-span-2">
                <Label>Provider mode</Label>
                <div data-testid="wizard-provider-mode" className="flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700">
                  LIVE — real calls through your connected providers
                </div>
                <p className="text-xs text-slate-500">
                  Live launches are blocked until credentials, agent sync, number sync, webhooks,
                  budget and contact validation all pass the deployment review.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Voice provider</Label>
                <Select value={form.voice_provider} onValueChange={(v) => set({ voice_provider: v })}>
                  <SelectTrigger data-testid="wizard-voice-provider"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="vapi">Vapi (live)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Telephony provider</Label>
                <Select value={form.telephony_provider} onValueChange={(v) => set({ telephony_provider: v })}>
                  <SelectTrigger data-testid="wizard-telephony-provider"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="voiso">Voiso (live · SIP trunk)</SelectItem>
                    <SelectItem value="illyvoip">IllyVoIP (live · SIP trunk)</SelectItem>
                    <SelectItem value="bitcall">Bitcall (live · outbound SIP trunk)</SelectItem>
                    <SelectItem value="mmdsmart">MMDSmart (live · SIP trunk)</SelectItem>
                    <SelectItem value="twilio">Twilio (live)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Outbound phone number</Label>
                <Select value={form.phone_number || undefined} onValueChange={(v) => set({ phone_number: v })}>
                  <SelectTrigger data-testid="wizard-phone-number"><SelectValue placeholder="Choose the caller ID for this campaign" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {(numbers || []).filter((n) => (n.status || "active") === "active").map((n) => (
                      <SelectItem key={n.id || n.phone} value={n.phone}>
                        {n.phone} · {(n.country || "").toUpperCase()} · {((n.languages || []).join(", ") || "any").toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  This campaign dials from the number you pick. Use one number for Italian calls and another for English.
                </p>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <span className="text-xs text-slate-500">Agent sync</span>
                  <SyncBadge status={selectedAgent?.sync_status || "not_connected"} testId="wizard-telephony-agent-sync" />
                  <span className="text-xs text-slate-500">Number sync</span>
                  <SyncBadge
                    status={(numbers || []).find((n) => n.phone === form.phone_number)?.sync_status || "not_connected"}
                    testId="wizard-telephony-number-sync"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Max concurrent calls</Label>
                <Input
                  data-testid="wizard-max-concurrent"
                  type="number"
                  min="1"
                  value={form.limits.max_concurrent_calls}
                  onChange={(e) => setNested("limits", { max_concurrent_calls: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Calls per minute</Label>
                <Input
                  data-testid="wizard-calls-per-minute"
                  type="number"
                  min="1"
                  value={form.limits.calls_per_minute}
                  onChange={(e) => setNested("limits", { calls_per_minute: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Max call duration (seconds)</Label>
                <Input
                  data-testid="wizard-max-duration"
                  type="number"
                  min="30"
                  value={form.limits.max_call_duration}
                  onChange={(e) => setNested("limits", { max_call_duration: Number(e.target.value) })}
                />
              </div>
            </div>
          ) : null}

          {step === 4 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Start date</Label>
                <Input
                  data-testid="wizard-start-date"
                  type="date"
                  value={form.schedule.start_date}
                  onChange={(e) => setNested("schedule", { start_date: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>End date</Label>
                <Input
                  data-testid="wizard-end-date"
                  type="date"
                  value={form.schedule.end_date}
                  onChange={(e) => setNested("schedule", { end_date: e.target.value })}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Allowed weekdays</Label>
                <div className="flex flex-wrap gap-2">
                  {WEEKDAYS.map((day, index) => (
                    <label
                      key={day}
                      className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
                    >
                      <Checkbox
                        data-testid={`wizard-weekday-${index}`}
                        checked={form.schedule.weekdays.includes(index)}
                        onCheckedChange={(checked) =>
                          setNested("schedule", {
                            weekdays: checked
                              ? [...form.schedule.weekdays, index].sort()
                              : form.schedule.weekdays.filter((d) => d !== index),
                          })
                        }
                      />
                      {day}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Local calling hours from</Label>
                <Input
                  data-testid="wizard-start-hour"
                  type="time"
                  value={form.schedule.start_hour}
                  onChange={(e) => setNested("schedule", { start_hour: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Local calling hours to</Label>
                <Input
                  data-testid="wizard-end-hour"
                  type="time"
                  value={form.schedule.end_hour}
                  onChange={(e) => setNested("schedule", { end_hour: e.target.value })}
                />
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <span className="text-sm text-slate-700">Respect contact timezone</span>
                <Switch
                  data-testid="wizard-respect-tz"
                  checked={form.schedule.respect_contact_timezone}
                  onCheckedChange={(v) => setNested("schedule", { respect_contact_timezone: v })}
                />
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <span className="text-sm text-slate-700">Retry after no answer</span>
                <Switch
                  data-testid="wizard-retry"
                  checked={form.schedule.retry_no_answer}
                  onCheckedChange={(v) => setNested("schedule", { retry_no_answer: v })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Maximum retry attempts</Label>
                <Input
                  data-testid="wizard-max-retries"
                  type="number"
                  min="0"
                  max="5"
                  value={form.schedule.max_retries}
                  onChange={(e) => setNested("schedule", { max_retries: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Voicemail behavior</Label>
                <Select
                  value={form.schedule.voicemail_behavior}
                  onValueChange={(v) => setNested("schedule", { voicemail_behavior: v })}
                >
                  <SelectTrigger data-testid="wizard-voicemail"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="leave_message">Leave a short message</SelectItem>
                    <SelectItem value="hang_up">Hang up immediately</SelectItem>
                    <SelectItem value="retry_later">Retry later</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : null}

          {step === 5 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Daily budget (USD)</Label>
                <Input
                  data-testid="wizard-daily-budget"
                  type="number"
                  step="0.5"
                  value={form.budget.daily_budget}
                  onChange={(e) => setNested("budget", { daily_budget: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Total campaign budget (USD)</Label>
                <Input
                  data-testid="wizard-total-budget"
                  type="number"
                  step="1"
                  value={form.budget.total_budget}
                  onChange={(e) => setNested("budget", { total_budget: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Maximum calls</Label>
                <Input
                  data-testid="wizard-max-calls"
                  type="number"
                  value={form.budget.max_calls}
                  onChange={(e) => setNested("budget", { max_calls: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Maximum cost per call (USD)</Label>
                <Input
                  data-testid="wizard-max-cost-per-call"
                  type="number"
                  step="0.05"
                  value={form.budget.max_cost_per_call}
                  onChange={(e) => setNested("budget", { max_cost_per_call: Number(e.target.value) })}
                />
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 md:col-span-2">
                <div>
                  <p className="text-sm font-medium text-slate-800">Automatic pause rules</p>
                  <p className="text-xs text-slate-500">
                    Pause the campaign automatically when the daily or total budget is reached.
                  </p>
                </div>
                <Switch
                  data-testid="wizard-auto-pause"
                  checked={form.budget.auto_pause_on_budget}
                  onCheckedChange={(v) => setNested("budget", { auto_pause_on_budget: v })}
                />
              </div>
            </div>
          ) : null}

          {step === 6 ? (
            <div className="space-y-4" data-testid="wizard-review">
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5" data-testid="wizard-sync-summary">
                <span className="kpi-label mr-1">Provider readiness</span>
                <ModeBadge mode={form.provider_mode} testId="wizard-mode-badge" />
                <span className="text-xs text-slate-500">Agent</span>
                <SyncBadge status={selectedAgent?.sync_status || "not_connected"} testId="wizard-agent-sync-badge" />
                <span className="text-xs text-slate-500">Number</span>
                <SyncBadge
                  status={(numbers || []).find((n) => n.phone === form.phone_number)?.sync_status || "not_connected"}
                  testId="wizard-number-sync-badge"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {[
                  ["Mode", form.provider_mode === "live" ? "LIVE — real provider calls" : "MOCK — simulated"],
                  ["Campaign", `${form.name || "—"} · ${form.product || "—"}`],
                  ["Market", `${form.country} · ${(form.language || "").toUpperCase()} · ${form.timezone}`],
                  ["Agent", selectedAgent ? `${selectedAgent.name} v${form.agent_version || selectedAgent.published_version}` : "—"],
                  ["Phone number", form.phone_number || "—"],
                  ["Contacts", `${form.contact_ids.length} valid`],
                  ["Concurrency", `${form.limits.max_concurrent_calls} concurrent · ${form.limits.calls_per_minute} cpm`],
                  ["Schedule", `${form.schedule.start_hour}–${form.schedule.end_hour} on ${form.schedule.weekdays.map((d) => WEEKDAYS[d]).join(", ")}`],
                  ["Budget", `${money(form.budget.daily_budget)} daily · ${money(form.budget.total_budget)} total`],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border border-slate-200 px-3 py-2">
                    <p className="kpi-label">{label}</p>
                    <p className="text-sm font-medium text-slate-800">{value}</p>
                  </div>
                ))}
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                <p className="text-sm text-slate-700">
                  Estimated maximum cost:{" "}
                  <span className="font-semibold text-slate-900" data-testid="wizard-estimated-cost">
                    {money(estimate.max)}
                  </span>{" "}
                  · expected {money(estimate.expected)} for {estimate.contacts} contacts
                </p>
              </div>

              {errors.length ? (
                <ul data-testid="wizard-errors" className="space-y-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5">
                  {errors.map((error) => (
                    <li key={error} className="text-sm text-rose-700">• {error}</li>
                  ))}
                </ul>
              ) : (
                <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700">
                  All required settings are valid. You can save as draft or schedule the campaign.
                </p>
              )}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
          <Button
            variant="outline"
            disabled={step === 0}
            onClick={() => setStep((s) => s - 1)}
            data-testid="wizard-back"
          >
            <ChevronLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!canNext()}
              onClick={() => setStep((s) => s + 1)}
              data-testid="wizard-next"
            >
              Continue <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={errors.length > 0 || save.isPending}
                onClick={() => save.mutate("draft")}
                data-testid="wizard-save-draft"
              >
                Save as draft
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                disabled={errors.length > 0 || save.isPending}
                onClick={() => save.mutate("scheduled")}
                data-testid="wizard-schedule"
              >
                Schedule campaign
              </Button>
            </div>
          )}
        </div>
      </Section>
    </>
  );
}